import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPictogrammeById } from '@/constants/pictogrammesChantier';
import { getCouleurByHex } from '@/constants/couleursChantier';

interface PreviewChantierProps {
  pictogrammeId: string | null;
  couleurHex: string | null;
  nomChantier?: string;
}

export function PreviewChantier({
  pictogrammeId,
  couleurHex,
  nomChantier,
}: PreviewChantierProps) {
  const pictogramme = pictogrammeId ? getPictogrammeById(pictogrammeId) : null;
  const couleur = couleurHex ? getCouleurByHex(couleurHex) : null;
  const IconComponent = pictogramme?.icon || HelpCircle;

  const hasSelection = pictogramme && couleur;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">Aperçu</p>
      <div
        className={cn(
          "flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed transition-all",
          hasSelection
            ? "border-border bg-card"
            : "border-muted-foreground/30 bg-muted/20"
        )}
      >
        {/* Icône dans un cercle */}
        <div
          className={cn(
            "w-16 h-16 rounded-2xl flex items-center justify-center mb-3 transition-all",
            hasSelection ? "bg-opacity-20" : "bg-muted"
          )}
          style={{
            backgroundColor: hasSelection
              ? `${couleurHex}20`
              : undefined,
          }}
        >
          <IconComponent
            className={cn(
              "h-8 w-8 transition-colors",
              !hasSelection && "text-muted-foreground"
            )}
            style={{
              color: hasSelection ? couleurHex! : undefined,
            }}
          />
        </div>

        {/* Label du pictogramme */}
        <p
          className={cn(
            "text-sm font-medium text-center transition-colors",
            hasSelection ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {pictogramme?.label || "Type de chantier"}
        </p>

        {/* Nom du chantier (optionnel) */}
        {nomChantier && (
          <p className="text-xs text-muted-foreground mt-1 text-center truncate max-w-full">
            {nomChantier}
          </p>
        )}

        {/* Indication couleur */}
        {couleur && (
          <div className="flex items-center gap-1.5 mt-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: couleurHex! }}
            />
            <span className="text-xs text-muted-foreground">
              {couleur.label}
            </span>
          </div>
        )}

        {/* Message si pas de sélection */}
        {!hasSelection && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Sélectionnez un pictogramme et une couleur
          </p>
        )}
      </div>
    </div>
  );
}
